import React, { useState } from 'react';
import { Box, Typography, IconButton, Avatar, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField } from '@mui/material';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CandidateProfileSidebar from './CandidateProfileSidebar';

// Helper function to parse individual elements from complex query strings
function parseQueryElements(queryString) {
  if (!queryString) return [];
  
  // Remove outer parentheses if present
  let cleanQuery = queryString.trim();
  if (cleanQuery.startsWith('(') && cleanQuery.endsWith(')')) {
    cleanQuery = cleanQuery.slice(1, -1);
  }
  
  // Split by OR and AND, then clean up each element
  const elements = cleanQuery
    .split(/\s+(?:OR|AND)\s+/)
    .map(element => element.trim())
    .filter(element => element.length > 0)
    .map(element => {
      // Remove quotes and extra parentheses
      return element.replace(/^['"`(]+|['"`)]+$/g, '').trim();
    })
    .filter(element => element.length > 0);
  
  return elements;
}

// Helper function to get readable name for an element
function getReadableName(element, queries) {
  // If it's a hex ID (24 characters, alphanumeric), try to look it up
  if (/^[a-f0-9]{24}$/i.test(element) && queries && queries[element]) {
    return queries[element].name || element;
  }
  
  // If it contains a colon, it might be a field:value query - extract the value part
  if (element.includes(':')) {
    const parts = element.split(':');
    if (parts.length > 1) {
      let value = parts[parts.length - 1].trim();
      // Clean up the value
      value = value.replace(/^['"`(]+|['"`)]+$/g, '');
      return value;
    }
  }
  
  // Return as-is if it's already readable
  return element;
}

// Helper function to get matching criteria for a profile
function getMatchingCriteria(profileId, macroMappingData) {
  if (!macroMappingData || !macroMappingData.queryResults || !macroMappingData.autoMacroMap) {
    return [];
  }

  // Find the query result for this profile
  const queryResult = macroMappingData.queryResults.find(result => result.profile_id === profileId);
  if (!queryResult || !queryResult.macro_ids) {
    return [];
  }

  // Process each macro_id to extract readable criteria
  const allCriteria = [];
  
  queryResult.macro_ids.forEach(macroId => {
    const macroValue = macroMappingData.autoMacroMap[macroId];
    if (!macroValue) return;

    // Parse the macro value to extract individual elements
    const elements = parseQueryElements(macroValue);
    
    elements.forEach(element => {
      const readableName = getReadableName(element, macroMappingData.queries);
      
      // Only add if it's meaningful (not just hex IDs or technical terms)
      if (readableName && 
          readableName.length > 2 && 
          !readableName.startsWith('profile_type:') &&
          !/^[a-f0-9]{24}$/i.test(readableName)) {
        allCriteria.push(readableName);
      }
    });
  });

  // Remove duplicates and return
  return [...new Set(allCriteria)];
}

const SimpleCandidateCard = ({ candidate, profileId, macroMappingData, onSendMessage, onRefreshResults, setMessages, messages }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [thumbsUpActive, setThumbsUpActive] = useState(false);
  const [thumbsDownActive, setThumbsDownActive] = useState(false);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [reason, setReason] = useState('');

  // Extract data from the candidate object
  const name = candidate?.candidate?.full_name ? unescape(candidate.candidate.full_name) : 'Jacky Harrison';
  
  // Get current title
  const title = candidate?.workexp
    ?.find(exp => exp.duration?.to_present)
    ?.projects?.map(proj => proj.role_and_group?.title)
    .filter(Boolean)[0] || 'Product Designer';
  
  // Get company name
  const company = candidate?.workexp
    ?.find(exp => exp.duration?.to_present)
    ?.company?.name || 'Google';
  
  // Get location
  const city = candidate?.candidate?.location?.city || 'Vancouver';
  const state = candidate?.candidate?.location?.state || 'BC';
  const country = candidate?.candidate?.location?.country || 'Canada';
  
  // Format location as "City, State, Country"
  let location = '';
  if (city && state && country) {
    location = `${city}, ${state}, ${country}`;
  } else if (city && country) {
    location = `${city}, ${country}`;
  } else if (city) {
    location = city;
  } else {
    location = 'Vancouver, BC, Canada';
  }

  // Generate initials for avatar if no image
  const getInitials = (fullName) => {
    return fullName
      .split(' ')
      .map(name => name.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleNameClick = () => {
    setSidebarOpen(true);
  };

  const handleSidebarClose = () => {
    setSidebarOpen(false);
  };

  const handleThumbsUp = () => {
    setThumbsUpActive(true);
    setThumbsDownActive(false);
    
    // No message needed for positive feedback - just visual confirmation
    console.log('SimpleCandidateCard: User liked candidate profile:', name);
  };

  const handleThumbsDown = () => {
    setThumbsUpActive(false);
    setThumbsDownActive(true);
    setShowReasonModal(true);
  };

  const handleReasonSubmit = () => {
    if (reason.trim()) {
      // Create user message and add it directly to the messages array
      const userMessage = {
        role: 'User',
        content: reason.trim(),
        id: `user_candidate_feedback_${Date.now()}`
      };
      
      // Add message to the messages array first, then trigger refresh
      if (setMessages) {
        setMessages(prev => {
          const updatedMessages = [...prev, userMessage];
          
          // Trigger search refresh with the complete updated messages array
          if (onRefreshResults) {
            console.log('SimpleCandidateCard: Triggering handleRefreshResults for candidate:', name);
            console.log('SimpleCandidateCard: Sending full message history with', updatedMessages.length, 'messages');
            // Use setTimeout to ensure the state update is processed
            setTimeout(() => {
              onRefreshResults(updatedMessages);
            }, 0);
          } else {
            console.warn('SimpleCandidateCard: onRefreshResults callback not available');
          }
          
          return updatedMessages;
        });
      }
    }
    
    setShowReasonModal(false);
    setReason('');
  };

  const handleReasonCancel = () => {
    setShowReasonModal(false);
    setReason('');
    setThumbsDownActive(false);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start', // Changed from 'center' to 'flex-start'
          gap: '16px',
          padding: '20px',
          border: '1px solid #E5E7EB',
          borderRadius: '12px',
          backgroundColor: '#FFFFFF',
          width: '100%',
          maxWidth: '100%', // Changed from fixed width
          minHeight: '120px', // Added minimum height
          minWidth: '800px', // Added minimum width to make it bigger
        }}
      >
        {/* Avatar */}
        <Avatar
          sx={{
            width: '64px',
            height: '64px',
            backgroundColor: '#FFFFFF',
            border: '2px solid #E5E7EB',
            color: '#6B7280',
            fontSize: '20px',
            fontWeight: 600,
            flexShrink: 0, // Prevent avatar from shrinking
          }}
        >
          {getInitials(name)}
        </Avatar>

        {/* Candidate Info */}
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '4px',
          minWidth: 0, // Allow content to shrink
          paddingRight: '16px', // Add padding to prevent overlap with buttons
        }}>
          <Typography
            onClick={handleNameClick}
            sx={{
              fontFamily: 'Roboto',
              fontWeight: 600,
              fontSize: '18px',
              color: '#6750A4',
              textDecoration: 'underline',
              cursor: 'pointer',
              '&:hover': {
                color: '#5a3d99',
              },
            }}
          >
            {name}
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Roboto',
              fontWeight: 400,
              fontSize: '16px',
              color: '#6B7280',
            }}
          >
            {title} at {company}
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Roboto',
              fontWeight: 400,
              fontSize: '14px',
              color: '#9CA3AF',
            }}
          >
            {location}
          </Typography>
          
          {/* Matching Criteria */}
          {(() => {
            // Debug logging
            console.log('SimpleCandidateCard Debug:', {
              profileId,
              macroMappingData: macroMappingData ? 'exists' : 'null',
              hasQueryResults: macroMappingData?.queryResults ? macroMappingData.queryResults.length : 0,
              hasAutoMacroMap: macroMappingData?.autoMacroMap ? Object.keys(macroMappingData.autoMacroMap).length : 0
            });

            const matchingCriteria = getMatchingCriteria(profileId, macroMappingData);
            console.log('Matching criteria for profile:', profileId, matchingCriteria);
            
            // Only show real matching criteria - no fallback
            if (matchingCriteria.length > 0) {
              return (
                <Box sx={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '4px', 
                  mt: 1,
                  maxWidth: '100%', // Ensure chips don't overflow
                }}>
                  {matchingCriteria.slice(0, 3).map((criterion, index) => (
                    <Chip
                      key={index}
                      label={criterion}
                      size="small"
                      icon={<CheckCircleIcon />}
                      color="success"
                      variant="outlined"
                      sx={{
                        fontSize: '0.65rem', // Made even smaller
                        height: '18px', // Made smaller
                        maxWidth: '150px', // Limit chip width
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        },
                        '& .MuiChip-icon': {
                          fontSize: '0.7rem', // Made smaller
                          width: '12px',
                          height: '12px',
                        }
                      }}
                    />
                  ))}
                  {matchingCriteria.length > 3 && (
                    <Chip
                      label={`+${matchingCriteria.length - 3} more`}
                      size="small"
                      color="default"
                      variant="outlined"
                      sx={{
                        fontSize: '0.65rem',
                        height: '18px'
                      }}
                    />
                  )}
                </Box>
              );
            }
            return null;
          })()}
        </Box>

        {/* Action Buttons */}
        <Box sx={{ 
          display: 'flex', 
          gap: '8px',
          flexShrink: 0, // Prevent buttons from shrinking
          alignSelf: 'flex-start', // Align to top
        }}>
          <IconButton
            onClick={handleThumbsUp}
            sx={{
              width: '40px',
              height: '40px',
              border: thumbsUpActive ? '2px solid #10B981' : '1px solid #E5E7EB',
              borderRadius: '8px',
              backgroundColor: thumbsUpActive ? '#ECFDF5' : '#FFFFFF',
              '&:hover': {
                backgroundColor: thumbsUpActive ? '#D1FAE5' : '#F3F4F6',
                borderColor: thumbsUpActive ? '#10B981' : '#3B82F6',
              },
            }}
          >
            <ThumbUpIcon sx={{ 
              fontSize: '20px', 
              color: thumbsUpActive ? '#10B981' : '#6B7280' 
            }} />
          </IconButton>
          <IconButton
            onClick={handleThumbsDown}
            sx={{
              width: '40px',
              height: '40px',
              border: thumbsDownActive ? '2px solid #EF4444' : '1px solid #E5E7EB',
              borderRadius: '8px',
              backgroundColor: thumbsDownActive ? '#FEF2F2' : '#FFFFFF',
              '&:hover': {
                backgroundColor: thumbsDownActive ? '#FEE2E2' : '#F3F4F6',
                borderColor: thumbsDownActive ? '#EF4444' : '#EF4444',
              },
            }}
          >
            <ThumbDownIcon sx={{ 
              fontSize: '20px', 
              color: thumbsDownActive ? '#EF4444' : '#6B7280' 
            }} />
          </IconButton>
        </Box>
      </Box>

      {/* Candidate Profile Sidebar */}
      <CandidateProfileSidebar
        open={sidebarOpen}
        onClose={handleSidebarClose}
        candidate={candidate}
        profileId={profileId}
        macroMappingData={macroMappingData}
      />

      {/* Thumbs Down Reason Modal */}
      <Dialog
        open={showReasonModal}
        onClose={handleReasonCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#FFFFFF',
            border: '1px solid #E2E8F0',
            borderRadius: '12px',
            padding: '8px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          }
        }}
      >
        <DialogTitle sx={{ 
          fontFamily: 'Roboto',
          fontWeight: 600,
          fontSize: '18px',
          color: '#1E293B',
          paddingBottom: '8px',
          paddingTop: '16px'
        }}>
          Why don't you like this candidate?
        </DialogTitle>
        <DialogContent sx={{ paddingTop: '8px' }}>
          <Typography sx={{ 
            fontFamily: 'Roboto',
            fontSize: '14px',
            color: '#64748B',
            marginBottom: '16px'
          }}>
            Please provide a reason for not liking {name}. This will help improve future candidate recommendations.
          </Typography>
          <TextField
            autoFocus
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            placeholder="e.g., Not enough experience with React, Wrong location, Salary expectations too high..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#FFFFFF',
                borderRadius: '8px',
                fontFamily: 'Roboto',
                '& fieldset': {
                  borderColor: '#D1D5DB',
                },
                '&:hover fieldset': {
                  borderColor: '#9CA3AF',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#6366F1',
                  borderWidth: '2px',
                },
              },
              '& .MuiInputBase-input': {
                color: '#374151',
                fontSize: '14px',
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#9CA3AF',
                opacity: 1,
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px 24px 24px', gap: '8px' }}>
          <Button
            onClick={handleReasonCancel}
            variant="outlined"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontFamily: 'Roboto',
              fontWeight: 500,
              borderColor: '#D1D5DB',
              color: '#374151',
              backgroundColor: '#FFFFFF',
              padding: '8px 16px',
              fontSize: '14px',
              '&:hover': {
                borderColor: '#9CA3AF',
                backgroundColor: '#F9FAFB',
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleReasonSubmit}
            variant="contained"
            disabled={!reason.trim()}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontFamily: 'Roboto',
              fontWeight: 500,
              backgroundColor: '#EF4444',
              color: '#FFFFFF',
              padding: '8px 16px',
              fontSize: '14px',
              border: 'none',
              '&:hover': {
                backgroundColor: '#DC2626',
              },
              '&:disabled': {
                backgroundColor: '#F3F4F6',
                color: '#9CA3AF',
              }
            }}
          >
            Submit Feedback
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SimpleCandidateCard;
